/**
 * LiveMapNowNext
 *
 * The headline above a controller's live map: what is watering, how much
 * longer it has, and what comes next — without clicking a polygon or reading
 * down a list of the whole day.
 *
 * Every countdown here is the VALVE's own, not its step's. A step's relays run
 * together but each carries its own duration, so a two-minute valve inside a
 * twenty-minute step has eighteen minutes less left than the step does. See
 * `buildLiveMapStatus`.
 *
 * "Watering" and "should be watering" are kept apart on purpose. The first is
 * the node's own relay bitmask; the second is a window that is open with
 * nothing heard from the node since it opened. A valve the node has actually
 * reported shut inside its window is neither — that is a dry block, and it
 * leads the panel as an error rather than sitting in the count.
 */

import { AlertTriangle, Clock, Droplets, Hand, HelpCircle, MapPin, Timer } from 'lucide-react';
import { formatRunTime } from '../../utils/irrigationRuns';
import { formatStartClock } from '../../utils/irrigationClock';
import type { LiveMapStatus, ValveStatus } from '../../utils/liveMapStatus';

interface LiveMapNowNextProps {
  status: LiveMapStatus;
  orgTimezone: string;
  /** Somewhere to send a click on a valve — usually "scroll the map to it". */
  onSelectValve?: (valve: ValveStatus) => void;
}

/** One valve that is open, or should be. */
function RunRow({
  valve,
  onSelect,
}: {
  valve: ValveStatus;
  onSelect?: (valve: ValveStatus) => void;
}) {
  const confirmed = valve.state === 'running' || valve.state === 'manual';
  const timed = valve.remainingSec != null && valve.durationSec != null;
  const pct = valve.progress != null ? Math.round(valve.progress * 100) : 0;

  const Wrapper = onSelect ? 'button' : 'div';

  return (
    <Wrapper
      {...(onSelect ? { type: 'button' as const, onClick: () => onSelect(valve) } : {})}
      className={`w-full rounded-xl border p-3 text-left transition ${
        confirmed
          ? 'border-blue-200 bg-blue-50/60 hover:border-blue-300'
          : 'border-dashed border-gray-300 bg-white hover:border-gray-400'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="mt-0.5 h-8 w-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: valve.zoneColor }}
          />
          <div className="min-w-0">
            <p className="truncate font-semibold text-gray-900">{valve.zoneName}</p>
            <p className="truncate text-xs text-gray-500">{valve.routerName}</p>
          </div>
        </div>
        <div className="shrink-0 text-right">
          {timed ? (
            <>
              <p
                className={`text-lg font-bold leading-tight tabular-nums ${
                  confirmed ? 'text-blue-600' : 'text-gray-600'
                }`}
              >
                {formatRunTime(valve.remainingSec!)}
              </p>
              <p className="text-[11px] uppercase tracking-wide text-gray-400">left</p>
            </>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-700">
              <Hand className="h-3 w-3" />
              Manual
            </span>
          )}
        </div>
      </div>

      {timed ? (
        <>
          <div
            className={`mt-2.5 h-2 w-full overflow-hidden rounded-full ${
              confirmed ? 'bg-blue-100' : 'bg-gray-200'
            }`}
          >
            <div
              className={`h-full rounded-full transition-[width] duration-1000 ease-linear ${
                confirmed ? 'bg-blue-500' : 'bg-gray-400'
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-1.5 flex items-center justify-between gap-2 text-xs tabular-nums text-gray-500">
            <span className="inline-flex items-center gap-1">
              <Droplets
                className={`h-3.5 w-3.5 ${confirmed ? 'text-blue-500' : 'text-gray-400'}`}
              />
              {confirmed ? 'Running' : 'Due'} {formatRunTime(valve.elapsedSec ?? 0)}
            </span>
            <span>of {formatRunTime(valve.durationSec ?? 0)}</span>
          </div>
        </>
      ) : (
        <p className="mt-2.5 text-xs text-gray-500">
          Open with no timed program running — it will stay on until something turns it off.
        </p>
      )}

      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-400">
        {!confirmed && (
          <span className="inline-flex items-center gap-1 text-gray-500">
            <HelpCircle className="h-3 w-3" />
            No valve report since it was due
          </span>
        )}
        {!valve.mapped && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            No area drawn
          </span>
        )}
      </div>
    </Wrapper>
  );
}

export default function LiveMapNowNext({
  status,
  orgTimezone,
  onSelectValve,
}: LiveMapNowNextProps) {
  const { watering, due, failed, skipped, upcoming, hasWindows } = status;
  const next = upcoming[0] ?? null;

  // "Watering" counts only what the nodes have actually reported open. A window
  // nobody has confirmed reads as "should be" — the distinction is the whole
  // reason both lists exist.
  const headline =
    watering.length > 0
      ? `${watering.length} zone${watering.length === 1 ? '' : 's'} watering now`
      : due.length > 0
        ? `${due.length} zone${due.length === 1 ? '' : 's'} should be watering now`
        : 'Nothing watering right now';

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 sm:p-4">
      {/* ── Headline ── */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
              watering.length > 0
                ? 'bg-blue-100 text-blue-600'
                : due.length > 0
                  ? 'bg-gray-100 text-gray-500'
                  : 'bg-gray-100 text-gray-400'
            }`}
          >
            <Droplets className="h-4 w-4" />
          </span>
          <p className="min-w-0 text-base font-semibold text-gray-900">{headline}</p>
        </div>

        {next ? (
          // Wraps, because a full-length zone name and a clock time do not
          // share a 320px row — the viewport suite caught exactly that.
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
            <Timer className="h-4 w-4 shrink-0 text-gray-400" />
            <span className="shrink-0 text-gray-500">Next</span>
            <span
              className="h-4 w-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: next.zoneColor }}
            />
            <span className="min-w-0 truncate font-medium text-gray-900">{next.zoneName}</span>
            <span className="shrink-0 tabular-nums text-gray-500">
              {formatStartClock(next.startsAtMs!, orgTimezone)} · in{' '}
              {formatRunTime(next.startsInSec!)}
            </span>
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            {hasWindows ? 'No further runs today' : 'No programs scheduled today'}
          </p>
        )}
      </div>

      {/* ── A program that did not fire leads, because a block is dry ── */}
      {failed.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {failed.map((valve) => (
            <div
              key={valve.key}
              className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p className="min-w-0">
                <span className="font-semibold">{valve.zoneName} did not start.</span>{' '}
                Its program has been running for {formatRunTime(valve.elapsedSec ?? 0)}, but{' '}
                {valve.routerName} reports the valve closed. Nothing is watering here.
              </p>
            </div>
          ))}
        </div>
      )}

      {/* ── A skipped sequence is water that did not happen ── */}
      {skipped.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {skipped.map((valve) => (
            <div
              key={valve.key}
              className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p className="min-w-0">
                <span className="font-semibold">{valve.zoneName} was skipped</span>{' '}
                {formatRunTime(valve.skippedAgoSec ?? 0)} ago — a node did not answer before it
                was due, so the valve never opened.
              </p>
            </div>
          ))}
        </div>
      )}

      {/* ── What is open, or should be ── */}
      {(watering.length > 0 || due.length > 0) && (
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {[...watering, ...due].map((valve) => (
            <RunRow key={valve.key} valve={valve} onSelect={onSelectValve} />
          ))}
        </div>
      )}

      {/* ── Up next, beyond the one in the headline ── */}
      {upcoming.length > 0 && (
        <div className="mt-3 border-t border-gray-100 pt-2">
          <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            <Clock className="h-3.5 w-3.5" />
            Up next today
          </p>
          <div className="divide-y divide-gray-100">
            {upcoming.slice(0, 4).map((valve) => (
              <div key={valve.key} className="flex items-center justify-between gap-3 py-1.5">
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="h-4 w-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: valve.zoneColor }}
                  />
                  <span className="truncate text-sm text-gray-800">{valve.zoneName}</span>
                  <span className="shrink-0 text-xs text-gray-400">
                    for {formatRunTime(valve.durationSec ?? 0)}
                  </span>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-medium tabular-nums text-gray-900">
                    {formatStartClock(valve.startsAtMs!, orgTimezone)}
                  </p>
                  <p className="text-xs tabular-nums text-gray-500">
                    in {formatRunTime(valve.startsInSec!)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
